// One andon, read in full before deciding whether to walk over: how urgent,
// who raised it and what they actually said, who owns it, whether the clock
// has run out.
//
// Read only, deliberately. Acknowledging happens in the Andon app, which owns
// the deadline clock and the escalation ladder; this sheet says what it knows
// and hands over. See the allow-list entry for andon.alert.

import type { AndonVM } from '@/api/types';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { fmtLocalTime } from '@/lib/pace';
import { andonHref, useAndon, useFlowlineShort } from '@/queries';
import { useStore } from '@/store';
import { Facts } from './QualitySheet';

export function AndonSheet({
  open,
  andonId,
  onClose,
  onGoToStation,
}: {
  open: boolean;
  andonId: number | null;
  onClose: () => void;
  onGoToStation: (workcentreId: number, name: string) => void;
}) {
  const t = useT();
  const isLeader = useStore((s) => s.isLeader);
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andon = useAndon(open ? andonId : null);
  const short = useFlowlineShort();
  const a: AndonVM | null = andon.data ?? null;
  const answered = !!a && a.state !== 'new';
  const href = a ? andonHref(andonAppUrl, a.id) : '#';

  return (
    <Sheet open={open} onClose={onClose} title={a?.title ?? t.andonRowTitleT} hideTitle>
      {!a ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton height={48} radius={12} />
          <Skeleton height={120} radius={12} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>
                {[a.ref, a.workcentreName, short(a.flowlineId)].filter(Boolean).join(' · ')}
              </span>
              <h2 style={{ font: "700 19px 'Space Grotesk',sans-serif", margin: '2px 0 0', lineHeight: 1.25 }}>{a.title}</h2>
            </span>
            <span
              style={{
                borderRadius: 7,
                padding: '3px 9px',
                font: '700 10.5px Manrope,sans-serif',
                background: answered ? 'var(--surface2)' : 'var(--danger)',
                color: answered ? 'var(--sub)' : '#fff',
                flex: 'none',
                marginTop: 4,
                textTransform: 'capitalize',
              }}
            >
              {/* Closing is the one act that ends an andon in interpod_andon
                  18.0.8; a withdrawn one is rare enough here to show as is. */}
              {a.state === 'closed' ? t.resolvedT : a.state === 'cancelled' ? a.state : answered ? t.respondingTagT : t.unansweredT}
            </span>
          </div>

          <Facts
            rows={[
              [
                t.urgencyT,
                `${'★'.repeat(a.stars)} ${a.stars === 3 ? t.lineStoppedT : a.stars === 2 ? t.stopsWithin2hT : t.needsAttentionT}`,
              ],
              [t.raisedByL, `${a.raisedByName || '—'} · ${fmtLocalTime(a.raisedAt)}`],
              ...(a.typeKey ? [[t.typeT, a.typeKey] as [string, string]] : []),
              ...(a.mo ? [[t.podT, a.mo] as [string, string]] : []),
              [t.ownerT, a.ownerName || t.nobodyYetT],
              ...(a.overdue && !answered ? [[t.slaT, t.ackTargetMissedT] as [string, string]] : []),
              ...(a.etaMinutes ? [['ETA', `${a.etaMinutes} ${t.minT}`] as [string, string]] : []),
            ]}
          />

          {a.words && (
            <div style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--ink)', lineHeight: 1.5, background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px', whiteSpace: 'pre-wrap' }}>
              “{a.words}”
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isLeader && !answered && (
              <a href={href} target="_blank" rel="noopener" className="press" style={primary}>
                {t.ackInAndonAppT}
                <Icon name="external" size={13} />
              </a>
            )}
            {a.workcentreId != null && (
              <button
                type="button"
                className="press"
                onClick={() => {
                  onClose();
                  onGoToStation(a.workcentreId!, a.workcentreName);
                }}
                style={outline}
              >
                {t.goToT} {a.workcentreName}
              </button>
            )}
            <a href={href} target="_blank" rel="noopener" className="press" style={outline}>
              {t.openInAndonAppT}
              <Icon name="external" size={13} />
            </a>
          </div>
        </div>
      )}
    </Sheet>
  );
}

const base: React.CSSProperties = {
  minHeight: 48,
  borderRadius: 13,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  font: '700 13.5px Manrope,sans-serif',
  textDecoration: 'none',
};
const primary: React.CSSProperties = { ...base, background: 'var(--ink)', color: 'var(--bg)' };
const outline: React.CSSProperties = { ...base, border: '1px solid var(--line)', color: 'var(--ink)', background: 'var(--surface)' };
